import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { Instrument, InstrumentQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useExchangeOptions } from '../hooks/useExchangeOptions'
import { useIndustryOptions } from '../hooks/useIndustryOptions'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Controls'
import { MultiSelect } from '../components/common/MultiSelect'
import type { MultiSelectOption } from '../components/common/MultiSelect'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import { kapitalisierung } from './priceFormat'

/** Das Recht, das diese Ansicht braucht. */
const PERMISSION = 'instrument:read'

const PAGE_SIZE = 50

/** Der Buchstabe aus der Datenbank, in Worten.
 *
 * Die Zuordnung steht HIER und nicht im Backend: `S` und `E` sind der
 * Speicherwert, "Aktie" und "ETF" sind Deutsch und damit Sache der Anzeige.
 * Die Schnittstelle bedient auch den MCP-Server, und der soll keine deutschen
 * Bezeichnungen ausliefern.
 *
 * Was hier fehlt, wird zum Buchstaben selbst — sichtbar statt verschwiegen.
 * Ein neuer Typ im Bestand fällt so beim Ansehen auf.
 */
const INSTRUMENT_TYPES: Record<string, string> = {
  S: 'Aktie',
  E: 'ETF',
  I: 'Index',
}

//: Dieselbe Zuordnung als Auswahl. Der Wert ist der Buchstabe der Datenbank,
//: das Label das deutsche Wort — die Suche greift auf beides zu.
const ART_OPTIONEN: MultiSelectOption[] = [
  { value: 'S', label: 'Aktie', hint: 'Einzelwerte' },
  { value: 'E', label: 'ETF', hint: 'Indexfonds' },
  // Der Index selbst, nicht ein Papier darauf. Er wird nicht gehandelt und
  // trägt deshalb weder Branche noch Unternehmenskennung.
  { value: 'I', label: 'Index', hint: 'DAX, S&P 500 …' },
]

//: Auswahl und Adresse: In der Adresse steht eine kommagetrennte Liste
//: (?exchange=XNAS,XETR), die Komponente arbeitet mit einem Array. Die
//: Adresse bleibt damit lesbar und teilbar, ohne dass useListParams Listen
//: kennen muss.
function alsListe(wert: string): string[] {
  return wert ? wert.split(',').filter(Boolean) : []
}

const COLUMNS: Column<Instrument>[] = [
  { key: 'name', label: 'Name', sortable: true, minWidth: '260px' },
  // Zwischen Name und ISIN: Die Art gehört zum Titel, nicht zur Kennung.
  //
  // Nicht sortierbar wäre naheliegend, ist aber falsch — nach Art zu sortieren
  // gruppiert Aktien und ETFs, und genau das will man in einer Liste mit
  // 67.000 Zeilen.
  { key: 'instrument_type', label: 'Art', sortable: true, minWidth: '90px',
    help: 'Aktie oder ETF. Stammt aus dem Instrumenten-Import; von Hand angelegte Titel tragen sie nicht.',
    render: (row) => row.instrument_type
      ? INSTRUMENT_TYPES[row.instrument_type] ?? row.instrument_type
      : '—' },
  // Kopierknopf: Die ISIN ist der Wert, den man von hier aus weitergibt — in
  // eine Suche, ein Ticket, eine Nachricht. Zwölf Stellen abzuschreiben ist
  // genau die Art Aufgabe, bei der ein Zeichen verrutscht.
  //
  // Ohne `copy`-Funktion wird der rohe Feldwert kopiert, und der ist hier
  // schon der richtige: Bei der ISIN gibt es keine Darstellung, die von der
  // Speicherung abweicht.
  { key: 'isin', label: 'ISIN', sortable: true, mono: true, copy: true,
    minWidth: '190px',
    help: 'Internationale Wertpapierkennnummer, zwölf Stellen: zwei Buchstaben Land, neun Zeichen Kennung, eine Prüfziffer. Sie ist eindeutig — ein Titel hat genau eine. Der Knopf rechts kopiert sie in die Zwischenablage.' },
  { key: 'primary_exchange', label: 'Primärhandelsplatz', sortable: true,
    mono: true,
    help: 'Die Börse, an der der Titel hauptsächlich gehandelt wird. Ein Titel kann an mehreren laufen; hier steht die führende.' },
  // Zwei Spalten, zwei Normen — nebeneinander und nicht als eine.
  //
  // Sie sind KEINE Alternativen zueinander: GICS fragt „womit verdient die
  // Firma ihr Geld", SIC fragt „als was hat sie sich bei der Börsenaufsicht
  // angemeldet". Amtliche Übersetzungen dazwischen gibt es nicht — eine Spalte
  // mit „der Branche" müsste raten, welche der beiden gerade gilt.
  //
  // Sortiert wird nach dem Namen, nicht nach dem Code: In der Spalte steht der
  // Name, und eine Namensliste in Codereihenfolge wirkt unsortiert. Titel ohne
  // Einordnung landen in beiden Richtungen am Ende.
  { key: 'sector_name', label: 'Sektor', sortable: true, minWidth: '190px',
    help: 'Der GICS-Sektor: elf Stufen, von Energie bis Immobilien. Er ordnet nach dem Geschäftszweck — womit ein Unternehmen sein Geld verdient. ETFs tragen keinen: Ein Fonds hält Titel, die einen haben, aber keinen eigenen. Die Namen sind englisch, weil sie so amtlich sind.',
    render: (row) => row.sector_name ?? '—' },
  { key: 'sic_name', label: 'SIC', sortable: true, minWidth: '190px',
    help: 'Die SIC-Branche — feiner als der Sektor und aus anderer Quelle: Sie steht in der Anmeldung bei der US-Börsenaufsicht. Deshalb tragen sie nur Emittenten mit US-Meldepflicht; für die übrigen bleibt sie leer.',
    render: (row) => row.sic_name ?? '—' },
  // ZWEI SPALTEN FÜR EINE GRÖSSE, weil sie Verschiedenes leisten.
  //
  // Die Klasse ist vergleichbar: „Large Cap" heißt in Seoul dasselbe wie in
  // New York. Der Betrag ist genauer, aber an seine Währung gebunden —
  // Samsungs 1.783 Bio. Won sind weniger als Apples 3,9 Bio. Dollar. Wer
  // sortiert, bekommt beim Betrag deshalb eine Reihenfolge über Währungen
  // hinweg, die nichts bedeutet; die Klasse sortiert sinnvoll.
  //
  // Der Betrag steht rechtsbündig: Zahlen liest man an der Stelle, nicht am
  // Anfang.
  { key: 'market_cap_class', label: 'Größe', sortable: true, minWidth: '110px',
    help: 'Die Größenklasse — von Mega Cap (über 200 Mrd) bis Nano Cap (unter 50 Mio). Sie ist über Börsen und Währungen hinweg vergleichbar, anders als der Betrag daneben. Nicht jeder Titel trägt eine: Die Quelle führt nur einen Teil des Bestands.',
    render: (row) => row.market_cap_class ?? '—' },
  // ANGEZEIGT WIRD DER ORIGINALBETRAG, SORTIERT NACH DEM DOLLARWERT.
  //
  // Beides zu zeigen wäre eine Spalte zu viel für dieselbe Aussage. Der
  // Originalbetrag ist das Gemessene und gehört in die Anzeige; der
  // Dollarwert ist umgerechnet und taugt zum Ordnen. Der Spaltenschlüssel
  // heißt deshalb nach dem, wonach sortiert wird.
  { key: 'market_cap_usd', label: 'Marktkapitalisierung', sortable: true,
    align: 'right', minWidth: '170px',
    help: 'Der Börsenwert: Anzahl der Aktien mal Kurs, in der Währung des Handelsplatzes. Sortiert wird nach dem in Dollar umgerechneten Wert — sonst stünde eine Reihe aus Won, Yen und Dollar nebeneinander, deren Reihenfolge nichts bedeutet.',
    render: (row) => kapitalisierung(row.market_cap, row.market_cap_ccy) },
]

export function InstrumentsPage() {
  const allowed = useHasPermission(PERMISSION)
  const navigate = useNavigate()
  const location = useLocation()

  // Vor `useListParams`, weil die Favoriten dort als Vorbelegung eingehen.
  const { options: platzOptionen, favoriteCodes, ready } =
    useExchangeOptions(allowed)
  // Ohne `ready`-Abhängigkeit: Der Branchenfilter hat keine Vorbelegung, also
  // muss die Liste nicht auf ihn warten. Sie zeigt in der Zehntelsekunde bis
  // zum Eintreffen nur ein leeres Auswahlfeld — kein falsches Ergebnis.
  const { options: branchenOptionen } = useIndustryOptions(allowed)

  // Der Zustand steht in der Adresse, nicht in der Komponente: teilbar,
  // überlebt das Neuladen, und die Zurück-Taste nimmt den letzten Schritt
  // zurück statt die Seite zu verlassen.
  const list = useListParams({
    filters: { q: '', exchange: '', type: '', industry: '' },
    // Die Ansicht startet auf den Favoriten unter den Handelsplätzen. Das ist
    // eine fachliche Entscheidung DIESER Seite und steht deshalb hier und
    // nicht im Hook: Wer alle 67.000 Titel sehen will, wählt sie ab — dann
    // steht ein Minus in der Adresse, und die Vorbelegung bleibt weg.
    preset: { exchange: favoriteCodes.join(',') },
    sort: 'name',
  })

  const params: InstrumentQuery = {
    q: list.filters.q.trim() || undefined,
    exchange: alsListe(list.filters.exchange),
    instrument_type: alsListe(list.filters.type),
    industry: alsListe(list.filters.industry),
    sort: list.sort as InstrumentQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
  }

  // Nicht abfragen, wenn das Recht ohnehin fehlt: Der Aufruf liefe garantiert
  // ins 403.
  // Erst wenn die Handelsplätze da sind: Vorher wäre die Vorbelegung leer,
  // die Abfrage liefe über alle 67.263 Titel und würde sofort von der
  // gefilterten abgelöst.
  const { result, loading, error, denied } = useListQuery(
    api.masterData, params, allowed && ready)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Instrumente" />
  }

  return (
    <>
      <FilterBar>
        <FilterGrow>
          {/*
            Der Hinweis steht im Platzhalter und nicht als eigene Zeile unter
            dem Feld: Ein Feld mit Hinweis ist höher als eines ohne, und in
            einer Zeile nebeneinander stünden die beiden Felder dann versetzt.
          */}
          <Input label="Suche"
                 placeholder="Name oder Symbol — mindestens zwei Zeichen"
                 value={list.filters.q} autoFocus
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        {/*
          Beide Filter als Mehrfachauswahl. Nichts gewählt heißt "alle" — nicht
          "keiner von diesen": Instrumente ohne Art bleiben in der Liste, sonst
          verschwänden von Hand angelegte Titel lautlos.
        */}
        <FilterFixed>
          <MultiSelect label="Art" options={ART_OPTIONEN}
                       selected={alsListe(list.filters.type)}
                       onChange={(werte) => list.setFilter('type', werte.join(','))}
                       placeholder="alle Arten" searchPlaceholder="Aktie, ETF …" />
        </FilterFixed>
        <FilterFixed>
          <MultiSelect label="Primärhandelsplatz" options={platzOptionen}
                       selected={alsListe(list.filters.exchange)}
                       onChange={(werte) => list.setFilter('exchange', werte.join(','))}
                       placeholder="alle Plätze"
                       searchPlaceholder="XNAS, NASDAQ oder New York …"
                       help="Gesucht wird über Kürzel, MIC und den vollen Namen. Seit der Umstellung auf ISO 10383 heißt Xetra XETR — wer das Kürzel kennt, muss den MIC nicht nachschlagen." />
        </FilterFixed>
        <FilterFixed>
          {/*
            EIN Feld für alle vier Codespalten. Jeder Eintrag trägt seine Norm
            im Wert (`GICS:10`), und die bestimmt, welche Spalte gefiltert
            wird — heute Sektor, sobald es Daten gibt auch SIC, NAICS und NACE.
            Die Zuordnung steht im Backend, wo auch die Spalten stehen.
          */}
          <MultiSelect label="Branche" options={branchenOptionen}
                       selected={alsListe(list.filters.industry)}
                       onChange={(werte) => list.setFilter('industry', werte.join(','))}
                       placeholder="alle Branchen"
                       searchPlaceholder="Energy, Health Care oder 45 …"
                       help="Gesucht wird über Name und Code. Die Bezeichnungen sind englisch — es sind die amtlichen GICS-Namen. Sobald weitere Normen im Baum stehen, greift dasselbe Feld auch auf sie." />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          // Die aktuelle Suche geht als Zustand mit, damit die Detailansicht
          // zurueck auf DIESELBE Liste fuehren kann — mit Filter, Sortierung
          // und Seite, nicht auf die nackte.
          onRowClick={(row) => navigate(`/instruments/${row.id}`,
                                        { state: { from: location.search } })}
          loading={loading && result === null}
          empty={<EmptyState title="keine Instrumente">
            {list.touched
              ? 'Suchbegriff kürzen oder Filter zurücksetzen.'
              : 'Es sind noch keine Instrumente eingetragen.'}
          </EmptyState>}
        />
      </Card>
    </>
  )
}
